interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeMB?: number;
}

/**
 * Gets file size in MB
 * @param bytes - File size in bytes
 * @returns number - File size in MB
 */
export const getFileSizeMB = (bytes: number): number => {
  return bytes / (1024 * 1024);
};

/**
 * Compresses an image to reduce file size while maintaining acceptable quality
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise<string> - Base64 string of compressed image
 */
export const compressImage = (
  file: File,
  options: CompressionOptions = {}
): Promise<string> => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.7,
    maxSizeMB = 5
  } = options;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw and compress image
      ctx?.drawImage(img, 0, 0, width, height);
      
      // Convert to blob with quality setting
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }

          // Check if the compressed image is still too large
          if (blob.size > maxSizeMB * 1024 * 1024) {
            // If still too large, compress further with lower quality
            const furtherCompressedQuality = Math.max(0.1, quality * 0.8);
            canvas.toBlob(
              (furtherBlob) => {
                if (!furtherBlob) {
                  reject(new Error('Failed to compress image further'));
                  return;
                }
                
                // Convert to base64
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => reject(new Error('Failed to read compressed image'));
                reader.readAsDataURL(furtherBlob);
              },
              'image/jpeg',
              furtherCompressedQuality
            );
          } else {
            // Convert to base64
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Failed to read compressed image'));
            reader.readAsDataURL(blob);
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    
    // Load image from file
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Compresses a base64 image string
 * @param base64String - The base64 image string to compress
 * @param options - Compression options
 * @returns Promise<string> - Base64 string of compressed image
 */
export const compressBase64Image = (
  base64String: string,
  options: CompressionOptions = {}
): Promise<string> => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.7,
    maxSizeMB = 5
  } = options;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw and compress image
      ctx?.drawImage(img, 0, 0, width, height);
      
      // Convert to blob with quality setting
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }

          // Check if the compressed image is still too large
          if (blob.size > maxSizeMB * 1024 * 1024) {
            // If still too large, compress further with lower quality
            const furtherCompressedQuality = Math.max(0.1, quality * 0.8);
            canvas.toBlob(
              (furtherBlob) => {
                if (!furtherBlob) {
                  reject(new Error('Failed to compress image further'));
                  return;
                }
                
                // Convert to base64
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => reject(new Error('Failed to read compressed image'));
                reader.readAsDataURL(furtherBlob);
              },
              'image/jpeg',
              furtherCompressedQuality
            );
          } else {
            // Convert to base64
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Failed to read compressed image'));
            reader.readAsDataURL(blob);
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = base64String;
  });
};

/**
 * Detects if the device is a mobile device
 * @returns boolean - True if mobile device
 */
export const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Gets appropriate compression settings based on device type
 * @returns CompressionOptions - Compression settings for the device
 */
export const getCompressionSettings = (): CompressionOptions => {
  if (isMobileDevice()) {
    // More aggressive compression for mobile devices
    return {
      maxWidth: 1280,
      maxHeight: 720,
      quality: 0.6,
      maxSizeMB: 4.5 // Leave some buffer below 5MB
    };
  } else {
    // Less aggressive compression for desktop
    return {
      maxWidth: 1920,
      maxHeight: 1080,
      quality: 0.8,
      maxSizeMB: 4.5
    };
  }
}; 